import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { JsonEditor } from 'json-edit-react';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import TextArea from '@decisiv/ui-components/lib/components/TextArea';
import { H2 } from '@decisiv/ui-components/lib/components/Typography';
import Times from '@decisiv/iconix/lib/components/Times';
import ExclamationCircleF from '@decisiv/iconix/lib/components/ExclamationCircleF';
import Share from '@decisiv/iconix/lib/components/Share';
import useJsonEditorTheme from '../../theme/jsonEditorTheme';
import { ToolkitButton } from './style';
import {
  CATEGORY_META,
  checkDraft7,
  parseServerError,
  prettyJSON,
  validateActionName,
} from './atUtils';

const DEFAULT_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: { data: { type: 'object', properties: {} } },
  required: ['data'],
};

// Shared container styling for the structured JsonEditor — used for the input,
// output and metadata fields so they look and behave identically.
const JSON_EDITOR_CONTAINER = {
  backgroundColor: 'var(--kds-bg-2)',
  fontFamily: 'var(--kds-mono)',
  fontSize: '12px',
};

// Renders a server-side save error as a structured card (title, field,
// summary, optional hint) with the raw message behind a disclosure.
function ServerErrorCard({ error }) {
  const parsed = parseServerError(error);

  if (!parsed) {
    return (
      <div className="at-err-card" style={{ marginTop: 12 }}>
        <div className="at-err-head">
          <ExclamationCircleF size="small" /> Save failed
        </div>
        <div className="at-err-summary">{error}</div>
      </div>
    );
  }

  return (
    <div className="at-err-card" style={{ marginTop: 12 }}>
      <div className="at-err-head">
        <ExclamationCircleF size="small" /> {parsed.title}
      </div>
      {parsed.field && (
        <div className="at-err-field">
          Field: <code>{parsed.field}</code>
        </div>
      )}
      {parsed.summary && <div className="at-err-summary">{parsed.summary}</div>}
      {parsed.hint && <div className="at-err-hint">{parsed.hint}</div>}
      {parsed.raw && (
        <details>
          <summary>Full server message</summary>
          <pre className="at-err-raw">{parsed.raw}</pre>
        </details>
      )}
    </div>
  );
}

ServerErrorCard.propTypes = {
  error: PropTypes.string.isRequired,
};

// CreateEditDrawer: right slide-in for Create (mode="create") or Edit
// (mode="edit"). Edit mode tracks original schemas to differentiate between
// in-place edits and "save as new version" — the dual CTA at the foot is the
// design's anchor: never auto-version on schema change.
export default function CreateEditDrawer({
  mode,
  action = null,
  version = null,
  allActions,
  onClose,
  onSave,
}) {
  const jsonEditorTheme = useJsonEditorTheme('github', JSON_EDITOR_CONTAINER);
  const isEdit = mode === 'edit';

  const [name, setName] = useState(isEdit ? action.name : '');
  // alias / description / category come from the selected version (the
  // action-level fallback covers legacy rows that don't carry them yet).
  const [alias, setAlias] = useState(
    isEdit ? version.alias || action.alias || '' : '',
  );
  const [description, setDescription] = useState(
    isEdit ? version.description || action.description || '' : '',
  );
  const [category, setCategory] = useState(
    isEdit ? version.category || action.category : 'Asset Management',
  );
  // Both schemas are edited via the same structured JsonEditor; the parsed
  // object is the source of truth on each side.
  const [inputObj, setInputObj] = useState(() =>
    isEdit ? version.inputSchema || DEFAULT_SCHEMA : DEFAULT_SCHEMA,
  );
  const [outputObj, setOutputObj] = useState(() =>
    isEdit ? version.outputSchema || DEFAULT_SCHEMA : DEFAULT_SCHEMA,
  );
  // Optional contract for Integration#metadata — empty object means "none".
  // Declared properties are auto-seeded onto new integrations by the backend.
  const [metadataObj, setMetadataObj] = useState(() =>
    isEdit ? version.metadataSchema || {} : {},
  );
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const orig = useRef(
    isEdit
      ? {
          inputObj: prettyJSON(version.inputSchema),
          outputObj: prettyJSON(version.outputSchema),
        }
      : null,
  );

  const schemaChanged =
    isEdit &&
    orig.current &&
    (prettyJSON(inputObj).trim() !== orig.current.inputObj.trim() ||
      prettyJSON(outputObj).trim() !== orig.current.outputObj.trim());
  const hasAssignedInts = isEdit && version.integrations.length > 0;

  const existingNames = useMemo(
    () =>
      allActions
        .filter((a) => !isEdit || a.id !== action.id)
        .map((a) => a.name),
    [allActions, isEdit, action],
  );

  const nameErr = !isEdit
    ? validateActionName(name.trim(), existingNames)
    : null;
  const aliasErr = !alias.trim() ? 'Alias is required' : null;
  const descErr =
    description.trim().length < 8
      ? 'Add a short description (min 8 chars)'
      : null;

  // JsonEditor only emits valid JSON on both sides, so we just need the
  // Draft-7 shape check per schema.
  const inputDraftErr = checkDraft7(inputObj);
  const outputDraftErr = checkDraft7(outputObj);
  const metadataDraftErr =
    metadataObj && Object.keys(metadataObj).length > 0
      ? checkDraft7(metadataObj)
      : null;

  const baseValid =
    !nameErr &&
    !aliasErr &&
    !descErr &&
    !inputDraftErr &&
    !outputDraftErr &&
    !metadataDraftErr;

  const nextVersionNum = isEdit
    ? Math.max(...action.versions.map((v) => v.version)) + 1
    : 1;

  const saveChangesBlocked = isEdit && schemaChanged && hasAssignedInts;
  const saveAsNewVersionEnabled = isEdit && schemaChanged;

  const submit = async (asNewVersion) => {
    if (!baseValid || submitting) return;
    setSubmitting(true);
    setServerError(null);

    let saveMode = 'create';
    if (isEdit) saveMode = asNewVersion ? 'new-version' : 'edit';
    const result = await onSave({
      mode: saveMode,
      data: {
        name: name.trim(),
        alias: alias.trim(),
        description: description.trim(),
        category,
        inputSchema: inputObj,
        outputSchema: outputObj,
        metadataSchema: metadataObj,
      },
    });

    if (result && result.errors) {
      setServerError(result.errors[0]?.detail || 'Save failed');
    }
    setSubmitting(false);
  };

  return (
    <>
      <div
        className="at-drawer-overlay"
        onClick={onClose}
        role="presentation"
      />
      <div
        className="at-drawer"
        role="dialog"
        aria-label={isEdit ? 'Edit Action' : 'Create Action'}
      >
        <div className="drawer-head">
          <div>
            <H2>{isEdit ? 'Edit action' : 'Create action'}</H2>
            <div className="head-meta">
              {isEdit ? (
                <>
                  Editing <code className="mono">{action.name}</code> · v
                  {version.version}
                </>
              ) : (
                <>
                  Define the contract. A `commands_flow` implementation lands
                  separately.
                </>
              )}
            </div>
          </div>
          <ToolkitButton
            variant="ghost"
            icon={Times}
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        <div className="drawer-body">
          <div className="at-section-title">Metadata</div>

          <div className="at-kds-field">
            <TextField
              id="action-name"
              name="action-name"
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="retrieve_part_pricing"
              disabled={isEdit}
              warningMessage={!isEdit && nameErr ? nameErr : undefined}
              helpMessage={
                isEdit
                  ? 'Name is locked. Rename requires creating a new action.'
                  : 'Verb-keyed, lowercase with underscores. Cannot be changed after creation.'
              }
            />
          </div>

          <div className="at-field-row">
            <div className="at-kds-field">
              <TextField
                id="action-alias"
                name="action-alias"
                label="Alias"
                required
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="Retrieve Part Pricing"
                warningMessage={aliasErr || undefined}
              />
            </div>
            <div className="at-field">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="at-label">Category</label>
              <div className="at-tag-picker">
                {CATEGORY_META.filter((c) => c.name !== 'Uncategorized').map(
                  (c) => {
                    const CatIcon = c.icon;
                    return (
                      <button
                        key={c.name}
                        type="button"
                        className={`tag-opt ${
                          category === c.name ? 'active' : ''
                        }`}
                        style={{ '--tag-bg': c.bg, '--tag-color': c.color }}
                        onClick={() => setCategory(c.name)}
                      >
                        <span className="tag-dot">
                          <CatIcon size="small" />
                        </span>
                        {c.name}
                      </button>
                    );
                  },
                )}
              </div>
            </div>
          </div>

          <div className="at-kds-field">
            <TextArea
              id="action-desc"
              name="action-desc"
              label="Description"
              required
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this action do? Include any provider/source caveats."
              warningMessage={descErr && description ? descErr : undefined}
            />
          </div>

          <div className="at-section-title" style={{ marginTop: 24 }}>
            Schemas
          </div>

          <div className="at-field">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="at-label">Input schema (JSON, Draft-7)</label>
            <div className="at-json-editor-host">
              <JsonEditor
                data={inputObj}
                setData={(next) => setInputObj(next)}
                rootName=""
                maxWidth="100%"
                theme={jsonEditorTheme}
              />
            </div>
            {inputDraftErr && (
              <div className="at-err">
                <ExclamationCircleF size="small" /> {inputDraftErr}
              </div>
            )}
          </div>
          <div className="at-field">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="at-label">Output schema (JSON, Draft-7)</label>
            <div className="at-json-editor-host">
              <JsonEditor
                data={outputObj}
                setData={(next) => setOutputObj(next)}
                rootName=""
                maxWidth="100%"
                theme={jsonEditorTheme}
              />
            </div>
            {outputDraftErr && (
              <div className="at-err">
                <ExclamationCircleF size="small" /> {outputDraftErr}
              </div>
            )}
          </div>

          <div className="at-field">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="at-label">
              Integration metadata schema (optional)
            </label>
            <div className="at-json-editor-host">
              <JsonEditor
                data={metadataObj}
                setData={(next) => setMetadataObj(next)}
                rootName=""
                maxWidth="100%"
                theme={jsonEditorTheme}
              />
            </div>
            {metadataDraftErr && (
              <div className="at-err">
                <ExclamationCircleF size="small" /> {metadataDraftErr}
              </div>
            )}
            <div className="at-hint">
              Declares the integration metadata keys this action expects. Each
              declared property is pre-filled (its default, else empty) when an
              integration is created for this action. Leave empty for none.
            </div>
          </div>

          {isEdit && saveChangesBlocked && (
            <div className="at-hint" style={{ marginTop: 12 }}>
              Schema changes must be saved as a new version when integrations
              are assigned — use “Save as v{nextVersionNum}”.
            </div>
          )}

          {serverError && <ServerErrorCard error={serverError} />}
        </div>

        <div className="drawer-foot">
          <ToolkitButton variant="ghost" text="Cancel" onClick={onClose} />
          <span className="spacer" />
          {!isEdit && (
            <ToolkitButton
              kind="primary"
              text="Create action"
              disabled={!baseValid || submitting}
              onClick={() => submit(false)}
            />
          )}
          {isEdit && (
            <>
              <ToolkitButton
                kind="secondary"
                text="Save changes"
                disabled={!baseValid || saveChangesBlocked || submitting}
                onClick={() => submit(false)}
              />
              <ToolkitButton
                kind="primary"
                icon={Share}
                text={`Save as v${nextVersionNum}`}
                disabled={!baseValid || !saveAsNewVersionEnabled || submitting}
                onClick={() => submit(true)}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
CreateEditDrawer.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  version: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  allActions: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
